import React, { useContext, useMemo } from 'react';
import { VenuesContext } from '../../contexts/VenuesContext';
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import moment from 'moment';

const VENUE_TYPE_COLORS = {
  Hall: '#1976d2',
  Outdoor: '#2e7d32',
  Stadium: '#ef6c00',
  Conference: '#9c27b0',
  default: '#607d8b',
};

const VenueStats = () => {
  const {
    totalBookings,
    mostBookedVenues,
    totalBookingAmount,
    venueTypeMonthlyBookings,
    loading,
    error,
  } = useContext(VenuesContext);

  /**
   * Normalize chart data:
   * One row per month, venueTypes stacked side-by-side
   */
  const venueTypeChartData = useMemo(() => {
    if(!venueTypeMonthlyBookings?.length) return[];

    const grouped = {};

    venueTypeMonthlyBookings.forEach(item => {
      const monthLabel = moment()
        .month(item.month - 1)
        .format('MMM');

      if (!grouped[monthLabel]) grouped[monthLabel] = { month: monthLabel };

      grouped[monthLabel][item.venueType] = item.bookingCount;
    });

    return Object.values(grouped);
  }, [venueTypeMonthlyBookings]);

  const venueTypes = useMemo(() => {
    if (!venueTypeMonthlyBookings?.length) return [];
    return [...new Set(venueTypeMonthlyBookings.map(c => c.venueType))];
  }, [venueTypeMonthlyBookings]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box mt={4}>
      {/* ===== Summary ===== */}
      <Typography variant="h5">Total Venue Bookings</Typography>
      <Typography variant="h4" color="primary">{totalBookings}</Typography>

      <Typography variant="h5" mt={2}>Total Booking Amount</Typography>
      <Typography variant="h4" color="secondary">
        Ksh.{(totalBookingAmount || 0).toLocaleString()}
      </Typography>

      {/* ===== Chart ===== */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" mb={2}>
          Monthly Bookings by Venue Type
        </Typography>

        <ResponsiveContainer width="100%" height={350}>
          <BarChart data={venueTypeChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            {venueTypes.map(type => (
              <Bar
                key={type}
                dataKey={type}
                fill={VENUE_TYPE_COLORS[type] || VENUE_TYPE_COLORS.default}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* ===== Most Booked Venues ===== */}
      <Typography variant="h5" mt={4}>
        Top 10 Most Booked Venues
      </Typography>

      <List>
        {mostBookedVenues.map((venue, index) => (
          <React.Fragment key={venue._id}>
            <ListItem>
              <ListItemText
                primary={`${index + 1}. ${venue.name} (${venue.venueType})`}
                secondary={`Bookings: ${venue.bookingCount} | Amount: Ksh.${(venue.totalAmount || 0).toLocaleString()}`}
              />
            </ListItem>
            {index < mostBookedVenues.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default VenueStats;
